/**
 * Depots Inventory Configuration
 *
 * @module core/modules/waste/config
 *
 * @description
 * Configuración del panel de inventario para depósitos usando BaseInventoryPanel.
 * Depósitos incluyen: cocheras, estaciones de transferencia, rellenos sanitarios, etc.
 */

import { useState, useCallback, useEffect } from 'react';
import type { InventoryPanelConfig, InventoryRow } from '../../../layouts/panels/LeftPanel';
import { wasteApi } from '../services/waste-api';
import { DEPOT_TYPES, DEPOT_STATUSES } from '../constants';
import type { WasteDepot } from '../types';
import { useWasteElementColorsStore } from '../store/element-colors-store';

/**
 * Traduce el tipo de depósito a su etiqueta en español
 */
function translateDepotType(value: string): string {
  const depot = DEPOT_TYPES.find(d => d.value === value);
  return depot?.label ?? value;
}

/**
 * Traduce el estado del depósito a su etiqueta en español
 */
function translateDepotStatus(value: string): string {
  const status = DEPOT_STATUSES.find(s => s.value === value);
  return status?.label ?? value;
}

/**
 * Formatea la capacidad del depósito
 */
function formatCapacity(value: number | null | undefined): string {
  if (value == null) return '-';
  return `${value.toLocaleString()} m³`;
}

/**
 * Hook para cargar depósitos desde la API
 *
 * @remarks
 * No existe un hook useDepots dedicado, por lo que se crea inline
 * siguiendo el patrón de useRoutes.
 */
function useDepotsData(projectId: string) {
  const [depots, setDepots] = useState<WasteDepot[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const loadDepots = useCallback(async () => {
    if (!projectId) {
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const data = await wasteApi.depots.getAll({ projectId });
      setDepots(data);
    } catch (err) {
      console.error('[useDepotsData] Error loading depots:', err);
      setError('Error al cargar depósitos');
    } finally {
      setIsLoading(false);
    }
  }, [projectId]);

  const deleteDepot = useCallback(async (id: string) => {
    try {
      await wasteApi.depots.delete(id);
      setDepots(prev => prev.filter(d => d.id !== id));
    } catch (err) {
      console.error('[useDepotsData] Error deleting depot:', err);
      throw err;
    }
  }, []);

  // Cargar al montar y cuando cambie projectId
  useEffect(() => {
    void loadDepots();
  }, [loadDepots]);

  // Escuchar eventos de CRUD
  useEffect(() => {
    const handleChange = (): void => {
      void loadDepots();
    };

    window.addEventListener('depot-created', handleChange);
    window.addEventListener('depot-updated', handleChange);
    window.addEventListener('depot-deleted', handleChange);

    return () => {
      window.removeEventListener('depot-created', handleChange);
      window.removeEventListener('depot-updated', handleChange);
      window.removeEventListener('depot-deleted', handleChange);
    };
  }, [loadDepots]);

  return { data: depots, isLoading, error, reload: loadDepots, delete: deleteDepot };
}

/**
 * Configuración del panel de inventario de depósitos
 */
export const depotsInventoryConfig: InventoryPanelConfig<WasteDepot> = {
  entityType: 'depot',
  displayName: 'Depósitos',
  displayNameSingular: 'depósito',

  narrowColumns: [
    { key: 'code', label: 'Código', title: 'Código del depósito', width: '70px' },
    { key: 'name', label: 'Nombre', title: 'Nombre del depósito', width: '1fr' },
  ],

  wideColumns: [
    { key: 'code', label: 'Cód', title: 'Código del depósito', width: '70px' },
    { key: 'name', label: 'Nombre', title: 'Nombre del depósito', width: '1.5fr' },
    { key: 'type', label: 'Tipo', title: 'Tipo de depósito', width: '130px', format: translateDepotType },
    { key: 'status', label: 'Estado', title: 'Estado operativo', width: '90px', format: translateDepotStatus },
    { key: 'capacity', label: 'Capacidad', title: 'Capacidad del depósito', width: '80px', format: formatCapacity },
  ],

  useData: (projectId: string) => useDepotsData(projectId),

  buildRow: (depot: WasteDepot): InventoryRow => ({
    id: depot.id,
    code: depot.code,
    name: depot.name,
    type: depot.depotType,
    status: depot.status,
    capacity: depot.capacity ?? null,
  }),

  events: {
    select: 'waste-depot-select',
    visibilityToggle: 'waste-depot-visibility-toggle',
    hover: 'waste-depot-hover',
    zoom: 'waste-depot-zoom',
    created: 'depot-created',
    updated: 'depot-updated',
    deleted: 'depot-deleted',
  },

  getZoomTarget: (depot) => ({
    type: 'point',
    coordinates: depot.geometry.coordinates,
  }),

  colorStore: {
    getColor: (key) => useWasteElementColorsStore.getState().getColor(key),
    setColor: (key, hex) => useWasteElementColorsStore.getState().setColor(key, hex),
    colorKey: 'depots',
  },

  detailsPanelType: 'depot',
  moduleId: 'waste',
};
